import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { uploadApi } from '../services/api';

interface MediaManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MediaConfig {
  [key: string]: {
    value: string;
    type: string;
    description: string;
  };
}

const MEDIA_ITEMS = [
  { key: 'wechat_group_qr', label: '微信群二维码', description: '用户扫码加入交流群' },
  { key: 'wechat_official_qr', label: '微信公众号二维码', description: '关注公众号获取更新' },
];

const MediaManager: React.FC<MediaManagerProps> = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState<MediaConfig>({});
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // 加载配置
  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/media-config', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setConfig(data.data);
      }
    } catch (error) {
      console.error('加载配置失败:', error);
      toast.error('加载配置失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理文件选择
  const handleFileSelect = async (key: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('请选择图片文件');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片大小不能超过5MB');
      return;
    }

    setUploadingKey(key);

    try {
      // 上传图片
      const uploadResult = await uploadApi.uploadImage(file);
      
      if (uploadResult.success && uploadResult.data?.url) {
        // 更新配置
        const token = localStorage.getItem('token');
        const response = await fetch('/api/admin/media-config', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            key,
            value: uploadResult.data.url,
          }),
        });

        const data = await response.json();
        if (data.success) {
          toast.success('更新成功');
          // 更新本地状态
          setConfig(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              value: uploadResult.data!.url,
            },
          }));
        } else {
          toast.error(data.message || '更新失败');
        }
      }
    } catch (error: any) {
      toast.error(error.message || '上传失败');
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                  <i className="fas fa-photo-video text-primary mr-3"></i>
                  媒体信息管理
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  管理网站的二维码和其他媒体资源
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {/* 内容 */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {MEDIA_ITEMS.map(item => (
                    <div
                      key={item.key}
                      className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {item.label}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {item.description}
                          </p>
                        </div>
                        <button
                          onClick={() => fileInputRefs.current[item.key]?.click()}
                          disabled={uploadingKey === item.key}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center text-sm"
                        >
                          {uploadingKey === item.key ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              上传中...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-upload mr-2"></i>
                              更换图片
                            </>
                          )}
                        </button>
                        <input
                          ref={el => { fileInputRefs.current[item.key] = el; }}
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleFileSelect(item.key, file);
                            }
                            e.target.value = '';
                          }}
                          className="hidden"
                        />
                      </div>

                      {/* 预览 */}
                      <div className="flex items-center gap-4">
                        <div className="w-32 h-32 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                          {config[item.key]?.value ? (
                            <img
                              src={config[item.key].value}
                              alt={item.label}
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <div className="text-gray-400 text-center">
                              <i className="fas fa-image text-3xl mb-2"></i>
                              <p className="text-xs">暂无图片</p>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">当前图片地址：</p>
                          <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block truncate">
                            {config[item.key]?.value || '未设置'}
                          </code>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 底部 */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                关闭
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MediaManager;

